import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Megaphone,
  Sparkles,
  CheckCircle2,
  Check,
  ArrowRight,
  ChevronRight,
  Send,
  Target,
  BarChart3,
  Layers,
  Globe,
  Tv,
  Share2,
  ShoppingBag,
  Award,
  TrendingUp,
  Sliders,
  PieChart,
  Calendar,
  Rocket,
} from "lucide-react";

export const Route = createFileRoute("/services_/campaign-design")({
  head: () => ({
    meta: [
      {
        title:
          "Integrated 360° Campaign Design Services — KMWAdvertising LLP",
      },
      {
        name: "description",
        content:
          "Integrated Campaign Design Services by KMWAdvertising LLP. 360-degree campaign planning, creative development, multi-channel execution, and performance optimization.",
      },
      {
        property: "og:title",
        content: "Integrated Campaign Design Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "360-degree integrated campaign design that creates impact across digital, TV, print, OOH, and retail touchpoints.",
      },
    ],
  }),
  component: CampaignDesignServicePage,
});

export function CampaignDesignServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const strategicPlanningList = [
    "Business Objective Analysis",
    "Brand Positioning Review",
    "Audience Research",
    "Consumer Behaviour Analysis",
    "Competitor Benchmarking",
    "Market Trend Analysis",
    "Media Consumption Insights",
    "Customer Journey Mapping",
    "Communication Objectives",
    "Campaign KPI Definition",
  ];

  const creativeDevelopmentList = [
    "Campaign Concept Creation",
    "Creative Direction",
    "Key Visual Design",
    "Campaign Messaging",
    "Brand Storytelling",
    "Advertising Themes",
    "Visual Identity Development",
    "Creative Mood Boards",
    "Campaign Taglines",
    "Campaign Slogans",
  ];

  const channelExecution = {
    digital: [
      "Google Ads",
      "Meta (Facebook & Instagram) Ads",
      "LinkedIn Advertising",
      "YouTube Campaigns",
      "Programmatic Display",
      "Native Advertising",
      "OTT Advertising",
      "Mobile App Advertising",
    ],
    social: [
      "Instagram Creatives",
      "Facebook Campaigns",
      "LinkedIn Content",
      "X (Twitter) Creatives",
      "YouTube Assets",
      "Short-form Video Content",
      "Stories & Reels",
      "Carousel Creatives",
    ],
    traditional: [
      "Television Commercials",
      "Print Advertisements",
      "Newspaper Campaigns",
      "Magazine Advertising",
      "Radio Campaigns",
      "Outdoor Advertising",
      "Transit Media",
      "Cinema Advertising",
    ],
    collateral: [
      "Brochures",
      "Flyers",
      "Posters",
      "Product Catalogues",
      "Event Branding",
      "Exhibition Graphics",
      "Retail Branding",
      "Point-of-Sale (POS) Materials",
    ],
  };

  const campaignTypes = [
    {
      title: "Brand Awareness Campaigns",
      desc: "Increase visibility and strengthen brand recognition through memorable storytelling and consistent messaging.",
    },
    {
      title: "Product Launch Campaigns",
      desc: "Introduce new products or services with coordinated multi-channel campaigns that generate excitement and drive market adoption.",
    },
    {
      title: "Seasonal Campaigns",
      desc: "Maximise engagement during festivals, holidays and shopping seasons with timely, relevant creative concepts.",
    },
    {
      title: "Performance Marketing Campaigns",
      desc: "Develop conversion-focused creative assets for Google Ads, Meta Ads, programmatic advertising and landing pages that drive measurable business outcomes.",
    },
    {
      title: "Corporate Campaigns",
      desc: "Promote organisational achievements, CSR initiatives, recruitment drives and investor communications with professional, brand-aligned campaigns.",
    },
    {
      title: "Retail & Promotional Campaigns",
      desc: "Drive footfall, online sales and customer engagement through tactical promotional campaigns, limited-time offers and retail activations.",
    },
    {
      title: "Event & Exhibition Campaigns",
      desc: "Create cohesive branding for conferences, trade shows, product launches and experiential marketing events.",
    },
  ];

  const metricsList = [
    "Campaign Reach",
    "Audience Engagement",
    "Click-Through Rate (CTR)",
    "Conversion Rate",
    "Lead Quality",
    "Cost per Acquisition (CPA)",
    "Return on Advertising Spend (ROAS)",
    "Brand Lift",
    "Social Engagement",
    "Creative Performance by Platform",
  ];

  const whyChooseList = [
    "360-degree integrated campaign planning",
    "Award-worthy creative concepts backed by strategy",
    "In-house design, copywriting, photography and video production",
    "Digital-first and traditional media expertise",
    "Performance-focused creative development",
    "AI-enhanced content production and optimisation",
    "Cross-channel campaign consistency",
    "Data-driven campaign measurement and reporting",
    "End-to-end execution from concept to launch",
    "Scalable solutions for startups, SMEs and enterprise brands",
  ];

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-rose-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <Link to="/services/branding-creatives" className="hover:text-white transition">
              Branding & Creatives
            </Link>
            <ChevronRight className="h-3 w-3 text-slate-600" />
            <span className="text-pink-400">Campaign Design</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Megaphone className="h-3.5 w-3.5" />
              360° Integrated Campaign Design
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Campaign Design Services
              <span className="block bg-gradient-to-r from-pink-400 via-rose-300 to-purple-300 bg-clip-text text-transparent mt-1">
                Integrated Campaign Design That Creates Impact Across Every Touchpoint
              </span>
            </h1>

            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl">
              <p>
                In today's fragmented media landscape, customers interact with brands across multiple platforms before making a purchase decision. A consumer may first discover your brand through social media, see a display advertisement, watch a YouTube video, receive an email, visit your website, encounter an outdoor billboard and finally complete a purchase after seeing a remarketing campaign.
              </p>
              <p className="text-sm sm:text-base text-slate-400">
                This complex customer journey requires more than individual advertisements. It demands a cohesive, strategically planned campaign that delivers a consistent message across every channel.
              </p>
              <p className="text-sm sm:text-base text-pink-300/90 font-medium">
                At KMWAdvertising LLP, we specialise in 360-degree Campaign Design, developing integrated marketing campaigns that connect creativity, strategy and media planning to maximise brand awareness, audience engagement and business results. With over 25 years of experience in advertising, digital marketing and media buying, we create campaigns that are not only visually compelling but also aligned with measurable marketing objectives.
              </p>
              <p className="text-xs sm:text-sm text-slate-400 italic">
                Whether you're launching a new brand, introducing a product, promoting an event, driving seasonal sales or increasing market share, our campaign design team develops ideas that resonate with audiences and perform across traditional and digital media.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#campaign-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Launch Your 360° Campaign</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#campaign-planning"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore Strategic Process</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Campaign Planning */}
      <section id="campaign-planning" className="py-20 md:py-28 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Target className="h-3.5 w-3.5" />
              Research & Strategy First
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Strategic Campaign Planning
            </h2>
            <p className="text-pink-300 font-semibold text-sm">Every Successful Campaign Begins with Insight</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Effective campaigns are built on research and strategy, not assumptions. Before developing creative concepts, we invest time in understanding your business, industry and audience. Our campaign planning process includes:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-8">
            {strategicPlanningList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition flex items-center gap-2.5"
              >
                <Check className="h-4 w-4 text-pink-400 shrink-0" />
                <span className="text-xs font-semibold text-white">{item}</span>
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-pink-300/90 italic font-semibold">
            This strategic foundation ensures that every campaign delivers relevant messaging to the right audience at the right time.
          </p>
        </div>
      </section>

      {/* Creative Campaign Development */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <Sparkles className="h-3.5 w-3.5" />
              Central Creative Concept
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Creative Campaign Development
            </h2>
            <p className="text-purple-300 font-semibold text-sm">Powerful Ideas That Inspire Action</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              The strongest campaigns are driven by a central creative concept that can be adapted across multiple platforms while maintaining consistency. Our creative development services include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {creativeDevelopmentList.map((item) => (
              <div
                key={item}
                className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-purple-500/40 transition"
              >
                {item}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-purple-300/90 italic font-semibold">
            Every concept is designed to strengthen brand recall, create emotional connections and encourage audience engagement.
          </p>
        </div>
      </section>

      {/* Multi-Channel Campaign Execution */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x space-y-12">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-mono font-bold uppercase">
              <Layers className="h-3.5 w-3.5" />
              Omnichannel Consistency
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Multi-Channel Campaign Execution
            </h2>
            <p className="text-rose-300 font-semibold text-sm">One Campaign. Multiple Platforms. Consistent Experience.</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Today's consumers move seamlessly between online and offline media. We design campaigns that maintain visual consistency and messaging across every customer touchpoint. Our campaign assets can be developed for:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Digital */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Globe className="h-4 w-4 text-pink-400" />
                Digital Advertising
              </h3>
              <ul className="space-y-1.5 text-xs text-slate-300">
                {channelExecution.digital.map((asset) => (
                  <li key={asset} className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-pink-400" />
                    {asset}
                  </li>
                ))}
              </ul>
            </div>

            {/* Social */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Share2 className="h-4 w-4 text-purple-400" />
                Social Media
              </h3>
              <ul className="space-y-1.5 text-xs text-slate-300">
                {channelExecution.social.map((asset) => (
                  <li key={asset} className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-purple-400" />
                    {asset}
                  </li>
                ))}
              </ul>
            </div>

            {/* Traditional */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Tv className="h-4 w-4 text-rose-400" />
                Traditional Media
              </h3>
              <ul className="space-y-1.5 text-xs text-slate-300">
                {channelExecution.traditional.map((asset) => (
                  <li key={asset} className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-rose-400" />
                    {asset}
                  </li>
                ))}
              </ul>
            </div>

            {/* Collateral */}
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 space-y-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <ShoppingBag className="h-4 w-4 text-emerald-400" />
                Brand Collateral
              </h3>
              <ul className="space-y-1.5 text-xs text-slate-300">
                {channelExecution.collateral.map((asset) => (
                  <li key={asset} className="flex items-center gap-2">
                    <span className="h-1 w-1 rounded-full bg-emerald-400" />
                    {asset}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="text-xs font-mono text-slate-400 italic">
            This integrated approach ensures customers receive a unified brand experience regardless of where they interact with your business.
          </p>
        </div>
      </section>

      {/* Campaign Types We Design */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Rocket className="h-3.5 w-3.5" />
              Tailored Objectives
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Campaign Types We Design
            </h2>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              Every campaign is tailored to specific business objectives and customer journeys. Our expertise includes:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {campaignTypes.map((type) => (
              <div
                key={type.title}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-pink-500/30 transition space-y-2"
              >
                <h3 className="text-base font-bold text-white">{type.title}</h3>
                <p className="text-xs text-slate-300 leading-relaxed">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Campaign Optimisation & Performance Analysis */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <BarChart3 className="h-3.5 w-3.5" />
              Continuous Optimisation
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Campaign Optimisation & Performance Analysis
            </h2>
            <p className="text-purple-300 font-semibold text-sm">Creativity Backed by Data</p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              A successful campaign doesn't end with launch. Continuous optimisation ensures that creative assets remain effective throughout the campaign lifecycle. We evaluate:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {metricsList.map((metric) => (
              <div
                key={metric}
                className="p-3.5 rounded-xl bg-white/5 border border-white/10 text-center text-xs font-bold text-slate-200 hover:border-purple-500/40 transition"
              >
                {metric}
              </div>
            ))}
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-r from-pink-950/30 via-rose-950/20 to-purple-950/30 border border-white/15 mb-16">
            <p className="text-sm sm:text-base text-slate-200 leading-relaxed font-medium">
              By combining creative expertise with analytics and performance marketing, we refine campaigns to maximise efficiency and improve return on investment.
            </p>
          </div>

          {/* Why Choose KMWAdvertising LLP for Campaign Design? */}
          <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-pink-950/50 via-[#0e0e1c] to-purple-950/40 border border-pink-500/30 space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-300 text-xs font-mono font-bold uppercase">
              <Award className="h-3.5 w-3.5" />
              25+ Years Advertising Excellence
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
              Why Choose KMWAdvertising LLP for Campaign Design?
            </h3>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
              A successful advertising campaign requires more than attractive visuals. It requires strategic thinking, market insight, creative excellence and media expertise. With over 25 years of advertising experience, KMWAdvertising LLP delivers integrated campaigns that combine creativity with measurable business outcomes.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {whyChooseList.map((strength) => (
                <div key={strength} className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-pink-400 shrink-0" />
                  <span className="text-xs sm:text-sm font-semibold text-slate-200">{strength}</span>
                </div>
              ))}
            </div>
            <p className="text-xs sm:text-sm text-pink-300/90 font-medium italic pt-4">
              Whether you're launching a new brand, expanding into new markets or running a nationwide advertising campaign, we design creative experiences that capture attention, strengthen brand perception and deliver lasting business impact.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="campaign-form" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-purple-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Integrated Campaign Brief
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Design Your Next 360° Campaign
              </h2>
              <p className="text-slate-300 text-sm">
                Connect with our campaign strategy directors to outline your multi-channel objectives and creative vision.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-pink-500/10 border border-pink-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-pink-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Campaign Brief Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our campaign strategy lead will contact you within 24 hours to schedule your kickoff meeting.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Ananya Roy"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Apex Consumer Care"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="ananya@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Campaign Type Focus</label>
                  <select className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition">
                    <option value="launch">Product Launch / Brand Awareness Campaign</option>
                    <option value="seasonal">Seasonal & Festive Promotion Campaign</option>
                    <option value="performance">Performance Marketing & Lead Generation Campaign</option>
                    <option value="corporate">Corporate / CSR / Investor Relations Campaign</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Campaign Goals & Channels</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about your campaign timeline, target channels, or key performance metrics..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-600 to-purple-600 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Submit Campaign Brief</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
